import type { App, Plugin } from 'vue'
import { createRouter, createWebHistory, RouteRecordRaw, type _RouterClassic } from 'vue-router'
import { handleHotUpdate, routes, type RouteNamedMap } from 'vue-router/auto-routes'
import { useAuthStore } from '@/shared/stores/AuthStore'
import { useAdminAccessStore } from '@/shared/stores/AdminAccessStore'
import type { AdminPermissionResource, InternalDepartment, InternalRoleName, PermissionLevel } from '@swipe4work/models-orval'

declare module 'vue-router' {
  interface RouteMeta {
    allowAnonymous?: boolean
    /** Marks the MSAL redirectUri page; auth-store init must never run there — see Router.ts beforeEach. */
    isAuthRedirect?: boolean
    /** Force a specific layout for this page; defaults to 'default' when logged in, 'empty' otherwise. */
    layout?: 'default' | 'empty'
    /** Page title shown in the topbar and used as the (last) breadcrumb label. */
    title?: string
    /** Route name of the parent page in the breadcrumb trail (that page supplies its own title/parent). */
    breadcrumbParent?: string
    /** Require a specific admin permission to enter this page. */
    permission?: {
      resource: AdminPermissionResource,
      level: PermissionLevel 
    }
    /** Require a specific internal role to enter this page. */
    role?: {
      name: InternalRoleName,
      department: InternalDepartment 
    }
    /** Require global (super-admin) permissions to enter this page. */
    requiresGlobal?: boolean
    /** Hide the layout breadcrumb bar on this page (e.g. the homepage). */
    hideBreadcrumb?: boolean
  }
}

const baseRoutes: RouteRecordRaw[] = [
  {
    path: '/redirect',
    name: 'auth.redirect',
    meta: { allowAnonymous: true },
    redirect: { name: 'auth.login' },
  },
]

const router: _RouterClassic = createRouter({
  routes: [ ...baseRoutes, ...routes ],
  history: createWebHistory(import.meta.env.BASE_URL),
})

declare module 'vue-router' {
  interface TypesConfig {
    Router: typeof router
    RouteNamedMap: RouteNamedMap
  }
}

router.beforeEach(async (to) => {
  /*
   * The MSAL redirectUri page (/auth/redirect) is also loaded in a hidden iframe for silent
   * token renewal. Booting the auth store there recreates a second MsalAuthService that races
   * the MSAL redirect-bridge (broadcastResponseToMainFrame) for the one-time auth response,
   * breaking silent renewal. That page only relays the response itself — it must never trigger
   * auth init or the permission checks below.
   */
  if (to.meta.isAuthRedirect) { return true }

  const authStore = useAuthStore()

  await authStore.init()

  // The store action keeps `isUserAuthenticated` in sync, which drives the layout in App.vue.
  const isAuthenticated = await authStore.isAuthenticated()

  if (!isAuthenticated) {
    if (!to.meta.allowAnonymous) {

      return { name: 'auth.login' }
    }

    return true
  }

  // Permission-gated pages: redirect to the homepage when the internal employee lacks access.
  const access = useAdminAccessStore()

  await access.init()

  if (to.meta.requiresGlobal && !access.globalPermissions) {
    return { path: '/' }
  }

  if (to.meta.permission && !access.hasPermission(to.meta.permission.resource, to.meta.permission.level)) {
    return { path: '/' }
  }

  if (to.meta.role && !access.hasRole(to.meta.role.name, to.meta.role.department)) {
    return { path: '/' }
  }

  return true
})

if (import.meta.hot) {
  handleHotUpdate(router)
}

export default {
  install(app: App) {
    app.use(router)
  },
} as Plugin
